import { Alert, Box, Typography } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import type { OrderSyncStatus } from "@features/orders-features/hooks/useOrdersStream";

const drivingTruckSx = {
  "@keyframes drive": {
    "0%": { transform: "translateX(0px) translateY(0px)" },
    "20%": { transform: "translateX(4px) translateY(-2px)" },
    "40%": { transform: "translateX(8px) translateY(0px)" },
    "60%": { transform: "translateX(12px) translateY(-2px)" },
    "80%": { transform: "translateX(16px) translateY(0px)", opacity: 1 },
    "92%": { transform: "translateX(20px) translateY(0px)", opacity: 0 },
    "93%": { transform: "translateX(0px) translateY(0px)", opacity: 0 },
    "100%": { transform: "translateX(0px) translateY(0px)", opacity: 1 },
  },
  animation: "drive 2s ease-in-out infinite",
} as const;

interface OrderSyncIndicatorProps {
  syncStatus: OrderSyncStatus;
  lastSyncedAt: Date | null;
  /** "banner" renders a full-width Alert (page header); "compact" renders an inline caption (widget/drawer). */
  variant?: "banner" | "compact";
  /**
   * When true, force the animated "syncing" state (e.g. during a background refetch)
   * so the truck reflects data-refresh activity, not only server sync-stream events.
   */
  isRefreshing?: boolean;
}

function bannerText(syncStatus: OrderSyncStatus, lastSyncedAt: Date | null): string {
  if (syncStatus === "updating") return "Syncing your latest orders...";
  // On error we keep showing the last known sync time rather than an alarming
  // failure message, since the previously loaded data is still displayed.
  if (syncStatus === "success") {
    return `Orders up to date · Last synced at ${lastSyncedAt?.toLocaleTimeString()}`;
  }
  return `Last synced at ${lastSyncedAt?.toLocaleTimeString()}`;
}

function compactText(syncStatus: OrderSyncStatus, lastSyncedAt: Date | null): string {
  if (syncStatus === "updating") return "Syncing orders...";
  // On error we keep showing the last known sync time rather than an alarming
  // failure message, since the previously loaded data is still displayed.
  if (syncStatus === "success") {
    return `Orders up to date · Last synced at ${lastSyncedAt?.toLocaleTimeString()}`;
  }
  return `Last synced at ${lastSyncedAt?.toLocaleTimeString()}`;
}

/**
 * Bouncing-truck "last refreshed" indicator driven by the orders sync stream.
 * Shared across the View Orders page (banner), the recent-orders widget, and the
 * order drawer (compact) so the refresh state reads identically everywhere.
 */
export function OrderSyncIndicator({
  syncStatus,
  lastSyncedAt,
  variant = "banner",
  isRefreshing = false,
}: Readonly<OrderSyncIndicatorProps>) {
  // A background refetch (15-min interval, window focus, or sync-stream invalidation)
  // should animate the truck too, so the indicator reflects refresh activity — not just
  // server-pushed sync events. An error state is preserved.
  const displayStatus: OrderSyncStatus =
    isRefreshing && syncStatus !== "error" ? "updating" : syncStatus;

  if (displayStatus === "idle") return null;

  const truck = (
    <Box
      sx={{
        width: 24,
        overflow: "hidden",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
      }}
    >
      <LocalShippingIcon
        fontSize={variant === "compact" ? "small" : "medium"}
        sx={displayStatus === "updating" ? drivingTruckSx : undefined}
      />
    </Box>
  );

  if (variant === "compact") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "text.secondary",
        }}
        role="status"
        aria-live="polite"
      >
        {truck}
        <Typography variant="caption" color="inherit" noWrap>
          {compactText(displayStatus, lastSyncedAt)}
        </Typography>
      </Box>
    );
  }

  return (
    <Alert severity={displayStatus === "updating" ? "info" : "success"} icon={truck}>
      {bannerText(displayStatus, lastSyncedAt)}
    </Alert>
  );
}

export default OrderSyncIndicator;
